import { EventCardDto, QueryEventsReq } from "@garther/interfaces";
import { Box, ListView, Spinner } from "@garther/ui/components";
import { cn, useI18n } from "@garther/ui/libs";
import { useNavigate } from "@tanstack/react-router";
import { Calendar } from "lucide-react";

import { useEventsPagination } from "@/features/event/hooks/use-events-query";

import { EventCard } from "./event-card";

type EventsProps = Partial<Omit<QueryEventsReq, "page">> & {
  classname?: string;
  enablePagination?: boolean;
};

export function Events({ classname, enablePagination = true, ...filters }: EventsProps) {
  const navigate = useNavigate();
  const { t } = useI18n();

  const { events, isLoading, pagination, setPage, currentPage } = useEventsPagination({
    enabled: true,
    ...filters,
    limit: filters.limit ?? 9
  });

  const handleEventSelect = (eventId: string) => {
    navigate({
      to: "/org/$orgId/event/$eventId",
      params: { orgId: filters.hostId ?? "", eventId }
    });
  };

  if (isLoading) {
    return (
      <div className="w-full min-h-[50vh] flex items-center justify-center">
        <Spinner />
      </div>
    );
  }

  if (!events || events.length === 0) {
    return (
      <Box className="w-full py-6 md:py-8 opacity-70">
        <div className="flex flex-col items-center justify-center text-center">
          <div className="mb-6">
            <div className="w-16 h-16 mx-auto bg-background-dark-secondary/10 rounded-full flex items-center justify-center">
              <Calendar className="w-8 h-8 text-text-secondary" strokeWidth={1.5} />
            </div>
          </div>
          <div className="space-y-2">
            <p className="text-responsive-lg font-normal text-text-primary">{t("org.events.empty.title")}</p>
            <p className="text-responsive-base text-text-secondary max-w-md">
              {t("org.events.empty.description")}
            </p>
          </div>
        </div>
      </Box>
    );
  }

  return (
    <ListView
      variant="pagination"
      items={events}
      renderItem={(event: EventCardDto.Type) => <EventCard event={event} onSelect={handleEventSelect} />}
      keyExtractor={(event: EventCardDto.Type) => event.id}
      containerClassName={cn(
        "grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 md:gap-3 lg:gap-4",
        classname
      )}
      className="space-y-4"
      currentPage={currentPage}
      totalPages={pagination?.totalPages ?? 1}
      onPageChange={setPage}
      showPagination={enablePagination}
    />
  );
}
